import React, { useState } from 'react';
import { 
  ArrowLeft, ArrowRight, Check, Loader2, CheckCircle, 
  User, Phone, Mail, MapPin, Clock, MessageSquare, Camera,
  Home, Building2, Briefcase, HelpCircle, Wrench, Quote, 
  Lightbulb, Sun, Battery, Flame, Car, Zap, X
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// WhatsApp configuration
const WHATSAPP_NUMBER = '61478453548';
const WHATSAPP_MESSAGE = "Hi Supa Energy Hub! I'd like to chat about...";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Primary Intent Options (ORDER MATTERS - Existing customer FIRST)
const primaryIntents = [
  { id: 'existing_customer', label: "I'm an existing customer", icon: User, description: 'Priority support for our customers', priority: true },
  { id: 'support_warranty', label: 'I need support or warranty', icon: Wrench, description: 'Issues, service requests, warranty' },
  { id: 'quote', label: 'I want a price or quote', icon: Quote, description: 'Get a free estimate' },
  { id: 'exploring', label: "I'm exploring / need advice", icon: Lightbulb, description: 'Learn about our solutions' },
];

// Existing Customer sub-options
const existingCustomerOptions = [
  { id: 'issue', label: 'I have an issue' },
  { id: 'warranty', label: 'Warranty / service request' },
  { id: 'general', label: 'General question' },
  { id: 'advice', label: 'System advice' },
  { id: 'upgrade', label: 'Upgrade options (battery / EV / heat pump)' },
  { id: 'not_sure', label: 'Not sure' },
];

// Support issue types
const supportIssueTypes = [
  { id: 'system_not_working', label: 'System not working' },
  { id: 'performance', label: 'Performance concern' },
  { id: 'monitoring', label: 'Monitoring / app issue' },
  { id: 'warranty_question', label: 'Warranty question' },
  { id: 'service_maintenance', label: 'Service / maintenance request' },
  { id: 'something_else', label: 'Something else' },
];

// Quote product options
const quoteProducts = [
  { id: 'solar', label: 'Solar only', icon: Sun },
  { id: 'battery', label: 'Battery only', icon: Battery },
  { id: 'solar_battery', label: 'Solar + Battery', icon: Zap },
  { id: 'heat_pump', label: 'Heat Pump', icon: Flame },
  { id: 'ev_charger', label: 'EV Charger', icon: Car },
  { id: 'not_sure', label: 'Not sure', icon: HelpCircle },
];

// Exploring interests (multi-select)
const exploringInterests = [
  { id: 'solar', label: 'Solar', icon: Sun },
  { id: 'battery', label: 'Battery', icon: Battery },
  { id: 'heat_pump', label: 'Heat Pump', icon: Flame },
  { id: 'ev_charger', label: 'EV Charger', icon: Car },
  { id: 'whole_home', label: 'Whole-home electrification', icon: Zap },
  { id: 'not_sure', label: 'Not sure', icon: HelpCircle },
];

// Property types
const propertyTypes = [
  { id: 'house', label: 'House', icon: Home },
  { id: 'townhouse', label: 'Townhouse', icon: Building2 },
  { id: 'business', label: 'Business', icon: Briefcase },
];

// Contact preferences
const contactMethods = [
  { id: 'call', label: 'Call', icon: Phone },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'sms', label: 'SMS', icon: MessageSquare },
];

// Best time options
const bestTimeOptions = [
  { id: 'morning', label: 'Morning' },
  { id: 'afternoon', label: 'Afternoon' },
  { id: 'evening', label: 'Evening' },
  { id: 'anytime', label: 'Anytime' },
];

// How to help options for exploring
const helpOptions = [
  { id: 'tips', label: 'Quick tips here' },
  { id: 'consultant', label: 'Talk to a consultant' },
  { id: 'estimate', label: 'Get a price estimate' },
];

const SupaHelpFlow = ({ source = 'widget', onClose, isFullPage = false }) => {
  const [currentStep, setCurrentStep] = useState('welcome');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [referenceId, setReferenceId] = useState('');
  const [error, setError] = useState('');
  const [attachments, setAttachments] = useState([]);
  
  // OTP verification state
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);

  const [formData, setFormData] = useState({
    primary_intent: '',
    category: '',
    sub_category: '',
    products: [],
    property_type: '',
    monthly_bill: '',
    installed_by_supa: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    preferred_contact: 'email',
    best_time: '',
    postcode: '',
    address: '',
    message: '',
    existing_customer: 'not_sure',
    how_to_help: '',
  });

  // Countdown timer for resend
  React.useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const sendOTP = async () => {
    if (!formData.phone) {
      setOtpError('Please enter your mobile number');
      return;
    }
    
    setOtpLoading(true);
    setOtpError('');
    
    try {
      const response = await fetch(`${API_URL}/api/supahelp/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: formData.phone }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setOtpSent(true);
        setResendCountdown(30);
      } else {
        setOtpError(data.detail || 'Failed to send verification code');
      }
    } catch (err) {
      setOtpError('Failed to send verification code. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      setOtpError('Please enter the 6-digit code');
      return;
    }
    
    setOtpLoading(true);
    setOtpError('');
    
    try {
      const response = await fetch(`${API_URL}/api/supahelp/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: formData.phone, code: otpCode }),
      });
      
      const data = await response.json();
      
      if (data.verified) {
        setOtpVerified(true);
        setOtpError('');
      } else {
        setOtpError(data.message || 'Invalid code. Please try again.');
      }
    } catch (err) {
      setOtpError('Verification failed. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Check if OTP is required for current flow
  const isOTPRequired = () => {
    // OTP required for Call/SMS contact methods OR existing customer/support flows
    if (formData.preferred_contact === 'call' || formData.preferred_contact === 'sms') {
      return true;
    }
    if (formData.primary_intent === 'existing_customer' || formData.primary_intent === 'support_warranty') {
      return true;
    }
    return false;
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const toggleProduct = (productId) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.includes(productId)
        ? prev.products.filter(p => p !== productId)
        : [...prev.products, productId]
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 3);
    setAttachments(files);
  };

  const goBack = () => {
    setError('');
    // Navigation logic based on current step
    const backMap = {
      'existing_customer_options': 'welcome',
      'support_issue_type': formData.primary_intent === 'existing_customer' ? 'existing_customer_options' : 'welcome',
      'support_installed_by': 'support_issue_type',
      'support_address': 'support_installed_by',
      'support_contact': formData.installed_by_supa === 'yes' ? 'support_address' : 'support_installed_by',
      'support_message': 'support_contact',
      'support_attachments': 'support_message',
      'quote_products': 'welcome',
      'quote_postcode': 'quote_products',
      'quote_bill': 'quote_postcode',
      'quote_existing': 'quote_bill',
      'quote_contact': 'quote_existing',
      'quote_message': 'quote_contact',
      'exploring_interests': 'welcome',
      'exploring_property': 'exploring_interests',
      'exploring_help': 'exploring_property',
      'exploring_contact': 'exploring_help',
      'contact_method': getContactMethodBackStep(),
      'contact_details': 'contact_method',
      'message_step': 'contact_details',
    };
    
    const prevStep = backMap[currentStep] || 'welcome';
    setCurrentStep(prevStep);
  };

  const getContactMethodBackStep = () => {
    if (formData.primary_intent === 'existing_customer') {
      return 'existing_customer_options';
    }
    return 'welcome';
  };

  const submitForm = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
      
      const submissionData = {
        source: source,
        page_url: pageUrl,
        primary_intent: formData.primary_intent,
        category: formData.category,
        sub_category: formData.sub_category,
        products: formData.products,
        property_type: formData.property_type,
        monthly_bill: formData.monthly_bill,
        installed_by_supa: formData.installed_by_supa,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        preferred_contact: formData.preferred_contact,
        best_time: formData.best_time,
        postcode: formData.postcode,
        address: formData.address,
        message: formData.message,
        existing_customer: formData.existing_customer,
        mobile_verified: otpVerified,
      };

      let response;
      
      if (attachments.length > 0) {
        // Submit with attachments
        const formPayload = new FormData();
        formPayload.append('data', JSON.stringify(submissionData));
        attachments.forEach(file => {
          formPayload.append('files', file);
        });
        
        response = await fetch(`${API_URL}/api/supahelp/submit-with-attachments`, {
          method: 'POST',
          body: formPayload,
        });
      } else {
        // Submit without attachments
        response = await fetch(`${API_URL}/api/supahelp/submit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(submissionData),
        });
      }

      if (response.ok) {
        const result = await response.json();
        setReferenceId(result.reference_id);
        setIsComplete(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const startOver = () => {
    setCurrentStep('welcome');
    setIsComplete(false);
    setReferenceId('');
    setError('');
    setAttachments([]);
    // Reset OTP state
    setOtpSent(false);
    setOtpCode('');
    setOtpVerified(false);
    setOtpError('');
    setResendCountdown(0);
    setFormData({
      primary_intent: '',
      category: '',
      sub_category: '',
      products: [],
      property_type: '',
      monthly_bill: '',
      installed_by_supa: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      preferred_contact: 'email',
      best_time: '',
      postcode: '',
      address: '',
      message: '',
      existing_customer: 'not_sure',
      how_to_help: '',
    });
  };

  // Render completion screen
  if (isComplete) {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 rounded-full bg-[#B5D43B]/20 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-[#B5D43B]" />
        </div>
        <h2 className="text-xl font-bold text-[#3D5A47] mb-2">Thanks — we've received your request</h2>
        <p className="text-gray-600 mb-4">We'll be in touch soon.</p>
        <div className="bg-gray-50 rounded-xl p-4 mb-4">
          <p className="text-sm text-gray-500 mb-1">Your reference number:</p>
          <p className="font-mono font-bold text-[#3D5A47] text-lg">{referenceId}</p>
        </div>
        <div className="flex gap-2 justify-center">
          <Button onClick={startOver} variant="outline" size="sm">
            Start Over
          </Button>
          {onClose && (
            <Button onClick={onClose} className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47]" size="sm">
              Close
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Option button component
  const OptionButton = ({ selected, onClick, children, priority = false }) => (
    <button
      onClick={onClick}
      className={`w-full p-3 rounded-xl border-2 text-left transition-all ${
        selected
          ? 'border-[#B5D43B] bg-[#B5D43B]/10'
          : priority
            ? 'border-[#B5D43B]/50 hover:border-[#B5D43B] bg-[#B5D43B]/5'
            : 'border-gray-200 hover:border-[#B5D43B]/50'
      }`}
    >
      {children}
    </button>
  );

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 'welcome':
        return (
          <div>
            <div className="text-center mb-4">
              <p className="text-2xl mb-1">👋</p>
              <h2 className="text-lg font-bold text-[#3D5A47]">Hi! I'm Supa Help</h2>
              <p className="text-gray-600 text-sm">How can we help you today?</p>
            </div>
            <div className="space-y-2">
              {primaryIntents.map((intent) => (
                <OptionButton
                  key={intent.id}
                  selected={formData.primary_intent === intent.id}
                  onClick={() => {
                    updateFormData('primary_intent', intent.id);
                    updateFormData('existing_customer', intent.id === 'existing_customer' ? 'yes' : 'not_sure');
                    // Navigate to next step
                    setTimeout(() => {
                      if (intent.id === 'existing_customer') {
                        setCurrentStep('existing_customer_options');
                      } else if (intent.id === 'support_warranty') {
                        setCurrentStep('support_issue_type');
                      } else if (intent.id === 'quote') {
                        setCurrentStep('quote_products');
                      } else if (intent.id === 'exploring') {
                        setCurrentStep('exploring_interests');
                      }
                    }, 150);
                  }}
                  priority={intent.priority}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      formData.primary_intent === intent.id ? 'bg-[#B5D43B]' : 'bg-gray-100'
                    }`}>
                      <intent.icon className={`w-5 h-5 ${
                        formData.primary_intent === intent.id ? 'text-white' : 'text-[#3D5A47]'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-[#3D5A47] text-sm">{intent.label}</p>
                      <p className="text-xs text-gray-500">{intent.description}</p>
                    </div>
                    {formData.primary_intent === intent.id && (
                      <Check className="w-5 h-5 text-[#B5D43B]" />
                    )}
                  </div>
                </OptionButton>
              ))}
            </div>
            
            {/* WhatsApp Quick Chat Option */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-500 text-center mb-2">Or chat with us instantly</p>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full p-3 rounded-xl bg-[#25D366] hover:bg-[#20bd5a] text-white font-medium transition-all"
              >
                <WhatsAppIcon className="w-5 h-5" />
                <span>Chat on WhatsApp</span>
              </a>
            </div>
          </div>
        );

      case 'existing_customer_options':
        return (
          <div>
            <h2 className="text-lg font-bold text-[#3D5A47] mb-1 text-center">What do you need help with?</h2>
            <p className="text-gray-600 text-sm text-center mb-4">Select an option below</p>
            <div className="space-y-2">
              {existingCustomerOptions.map((option) => (
                <OptionButton
                  key={option.id}
                  selected={formData.category === option.id}
                  onClick={() => {
                    updateFormData('category', option.id);
                    setTimeout(() => {
                      if (option.id === 'issue' || option.id === 'warranty') {
                        setCurrentStep('support_issue_type');
                      } else if (option.id === 'upgrade') {
                        setCurrentStep('quote_products');
                      } else {
                        setCurrentStep('contact_method');
                      }
                    }, 150);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#3D5A47]">{option.label}</span>
                    {formData.category === option.id && <Check className="w-5 h-5 text-[#B5D43B]" />}
                  </div>
                </OptionButton>
              ))}
            </div>
          </div>
        );

      case 'support_issue_type':
        return (
          <div>
            <h2 className="text-lg font-bold text-[#3D5A47] mb-1 text-center">What best describes the issue?</h2>
            <p className="text-gray-600 text-sm text-center mb-4">This helps us route your request</p>
            <div className="space-y-2">
              {supportIssueTypes.map((type) => (
                <OptionButton
                  key={type.id}
                  selected={formData.sub_category === type.id}
                  onClick={() => {
                    updateFormData('sub_category', type.id);
                    setTimeout(() => setCurrentStep('support_installed_by'), 150);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#3D5A47]">{type.label}</span>
                    {formData.sub_category === type.id && <Check className="w-5 h-5 text-[#B5D43B]" />}
                  </div>
                </OptionButton>
              ))}
            </div>
          </div>
        );

      case 'support_installed_by':
        return (
          <div>
            <h2 className="text-lg font-bold text-[#3D5A47] mb-1 text-center">Was your system installed by Supa Energy Hub?</h2>
            <div className="space-y-2 mt-4">
              {[
                { id: 'yes', label: 'Yes' },
                { id: 'no', label: 'No' },
                { id: 'not_sure', label: 'Not sure' },
              ].map((option) => (
                <OptionButton
                  key={option.id}
                  selected={formData.installed_by_supa === option.id}
                  onClick={() => {
                    updateFormData('installed_by_supa', option.id);
                    setTimeout(() => {
                      if (option.id === 'yes') {
                        setCurrentStep('support_address');
                      } else {
                        setCurrentStep('support_contact');
                      }
                    }, 150);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#3D5A47]">{option.label}</span>
                    {formData.installed_by_supa === option.id && <Check className="w-5 h-5 text-[#B5D43B]" />}
                  </div>
                </OptionButton>
              ))}
            </div>
          </div>
        );

      case 'support_address':
        return (
          <div>
            <h2 className="text-lg font-bold text-[#3D5A47] mb-1 text-center">Installation address</h2>
            <p className="text-gray-600 text-sm text-center mb-4">This helps us find your system details</p>
            <div className="space-y-3">
              <div>
                <Label htmlFor="address" className="text-[#3D5A47] text-sm">Address</Label>
                <Input
                  id="address"
                  placeholder="123 Main Street, Suburb VIC"
                  value={formData.address}
                  onChange={(e) => updateFormData('address', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="postcode" className="text-[#3D5A47] text-sm">Postcode</Label>
                <Input
                  id="postcode"
                  placeholder="3000"
                  value={formData.postcode}
                  onChange={(e) => updateFormData('postcode', e.target.value)}
                  className="mt-1"
                  maxLength={4}
                />
              </div>
            </div>
            <Button
              onClick={() => setCurrentStep('support_contact')}
              className="w-full mt-4 bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47]"
              disabled={!formData.address || !formData.postcode}
            >
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      case 'support_contact':
        return renderContactStep(() => setCurrentStep('support_message'));

      case 'support_message':
        return (
          <div>
            <h2 className="text-lg font-bold text-[#3D5A47] mb-1 text-center">Describe the issue</h2>
            <p className="text-gray-600 text-sm text-center mb-4">Please provide as much detail as possible</p>
            <Textarea
              placeholder="Please describe the issue you're experiencing..."
              value={formData.message}
              onChange={(e) => updateFormData('message', e.target.value)}
              className="min-h-[120px]"
            />
            <Button
              onClick={() => setCurrentStep('support_attachments')}
              className="w-full mt-4 bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47]"
              disabled={!formData.message}
            >
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      case 'support_attachments':
        return (
          <div>
            <h2 className="text-lg font-bold text-[#3D5A47] mb-1 text-center">Add photos (optional)</h2>
            <p className="text-gray-600 text-sm text-center mb-4">Photos can help us understand the issue better</p>
            <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center">
              <Camera className="w-10 h-10 text-gray-400 mx-auto mb-2" />
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer text-sm text-[#3D5A47] hover:text-[#B5D43B]"
              >
                <span className="font-medium">Click to upload</span> or drag and drop
              </label>
              <p className="text-xs text-gray-500 mt-1">Up to 3 images (PNG, JPG)</p>
            </div>
            {attachments.length > 0 && (
              <div className="mt-3 space-y-2">
                {attachments.map((file, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 p-2 rounded">
                    <Check className="w-4 h-4 text-[#B5D43B]" />
                    <span className="truncate">{file.name}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 mt-4">
              <Button
                onClick={submitForm}
                className="flex-1 bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47]"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </div>
          </div>
        );

      case 'quote_products':
        return (
          <div>
            <h2 className="text-lg font-bold text-[#3D5A47] mb-1 text-center">What are you looking for?</h2>
            <p className="text-gray-600 text-sm text-center mb-4">Select a product</p>
            <div className="grid grid-cols-2 gap-2">
              {quoteProducts.map((product) => (
                <OptionButton
                  key={product.id}
                  selected={formData.products.includes(product.id)}
                  onClick={() => {
                    updateFormData('products', [product.id]);
                    setTimeout(() => setCurrentStep('quote_postcode'), 150);
                  }}
                >
                  <div className="flex flex-col items-center py-2">
                    <product.icon className={`w-6 h-6 mb-1 ${
                      formData.products.includes(product.id) ? 'text-[#B5D43B]' : 'text-gray-400'
                    }`} />
                    <span className="text-xs font-medium text-[#3D5A47] text-center">{product.label}</span>
                  </div>
                </OptionButton>
              ))}
            </div>
          </div>
        );

      case 'quote_postcode':
        return (
          <div>
            <h2 className="text-lg font-bold text-[#3D5A47] mb-1 text-center">What's your postcode?</h2>
            <p className="text-gray-600 text-sm text-center mb-4">So we can check eligibility and rebates</p>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="3000"
                value={formData.postcode}
                onChange={(e) => updateFormData('postcode', e.target.value)}
                className="pl-10 text-center text-lg"
                maxLength={4}
              />
            </div>
            <Button
              onClick={() => setCurrentStep('quote_bill')}
              className="w-full mt-4 bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47]"
              disabled={!formData.postcode || formData.postcode.length < 4}
            >
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      case 'quote_bill':
        return (
          <div>
            <h2 className="text-lg font-bold text-[#3D5A47] mb-1 text-center">Average monthly electricity bill?</h2>
            <p className="text-gray-600 text-sm text-center mb-4">Optional — helps us size correctly</p>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
              <Input
                placeholder="e.g. 220"
                value={formData.monthly_bill}
                onChange={(e) => updateFormData('monthly_bill', e.target.value)}
                className="pl-8"
                type="number"
              />
            </div>
            <div className="flex gap-2 mt-4">
              <Button
                onClick={() => setCurrentStep('quote_existing')}
                variant="outline"
                className="flex-1"
              >
                Skip
              </Button>
              <Button
                onClick={() => setCurrentStep('quote_existing')}
                className="flex-1 bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47]"
              >
                Continue
              </Button>
            </div>
          </div>
        );

      case 'quote_existing':
        return (
          <div>
            <h2 className="text-lg font-bold text-[#3D5A47] mb-1 text-center">Are you an existing customer?</h2>
            <div className="space-y-2 mt-4">
              {[
                { id: 'yes', label: 'Yes' },
                { id: 'no', label: 'No' },
                { id: 'not_sure', label: 'Not sure' },
              ].map((option) => (
                <OptionButton
                  key={option.id}
                  selected={formData.existing_customer === option.id}
                  onClick={() => {
                    updateFormData('existing_customer', option.id);
                    setTimeout(() => setCurrentStep('quote_contact'), 150);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#3D5A47]">{option.label}</span>
                    {formData.existing_customer === option.id && <Check className="w-5 h-5 text-[#B5D43B]" />}
                  </div>
                </OptionButton>
              ))}
            </div>
          </div>
        );

      case 'quote_contact':
        return renderContactStep(() => setCurrentStep('quote_message'));

      case 'quote_message':
        return (
          <div>
            <h2 className="text-lg font-bold text-[#3D5A47] mb-1 text-center">Anything specific for your quote?</h2>
            <p className="text-gray-600 text-sm text-center mb-4">Optional</p>
            <Textarea
              placeholder="e.g., I'd like to include a battery, my roof faces north..."
              value={formData.message}
              onChange={(e) => updateFormData('message', e.target.value)}
              className="min-h-[100px]"
            />
            <div className="flex gap-2 mt-4">
              <Button
                onClick={submitForm}
                className="w-full bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47]"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </div>
          </div>
        );

      case 'exploring_interests':
        return (
          <div>
            <h2 className="text-lg font-bold text-[#3D5A47] mb-1 text-center">What are you interested in?</h2>
            <p className="text-gray-600 text-sm text-center mb-4">Select all that apply</p>
            <div className="grid grid-cols-2 gap-2">
              {exploringInterests.map((interest) => (
                <OptionButton
                  key={interest.id}
                  selected={formData.products.includes(interest.id)}
                  onClick={() => toggleProduct(interest.id)}
                >
                  <div className="flex flex-col items-center py-2">
                    <interest.icon className={`w-6 h-6 mb-1 ${
                      formData.products.includes(interest.id) ? 'text-[#B5D43B]' : 'text-gray-400'
                    }`} />
                    <span className="text-xs font-medium text-[#3D5A47] text-center">{interest.label}</span>
                  </div>
                </OptionButton>
              ))}
            </div>
            <Button
              onClick={() => setCurrentStep('exploring_property')}
              className="w-full mt-4 bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47]"
              disabled={formData.products.length === 0}
            >
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      case 'exploring_property':
        return (
          <div>
            <h2 className="text-lg font-bold text-[#3D5A47] mb-1 text-center">Property type</h2>
            <div className="grid grid-cols-3 gap-2 mt-4">
              {propertyTypes.map((type) => (
                <OptionButton
                  key={type.id}
                  selected={formData.property_type === type.id}
                  onClick={() => {
                    updateFormData('property_type', type.id);
                    setTimeout(() => setCurrentStep('exploring_help'), 150);
                  }}
                >
                  <div className="flex flex-col items-center py-2">
                    <type.icon className={`w-6 h-6 mb-1 ${
                      formData.property_type === type.id ? 'text-[#B5D43B]' : 'text-gray-400'
                    }`} />
                    <span className="text-xs font-medium text-[#3D5A47]">{type.label}</span>
                  </div>
                </OptionButton>
              ))}
            </div>
          </div>
        );

      case 'exploring_help':
        return (
          <div>
            <h2 className="text-lg font-bold text-[#3D5A47] mb-1 text-center">How would you like help?</h2>
            <div className="space-y-2 mt-4">
              {helpOptions.map((option) => (
                <OptionButton
                  key={option.id}
                  selected={formData.how_to_help === option.id}
                  onClick={() => {
                    updateFormData('how_to_help', option.id);
                    setTimeout(() => {
                      if (option.id === 'estimate') {
                        setCurrentStep('quote_postcode');
                      } else {
                        setCurrentStep('exploring_contact');
                      }
                    }, 150);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#3D5A47]">{option.label}</span>
                    {formData.how_to_help === option.id && <Check className="w-5 h-5 text-[#B5D43B]" />}
                  </div>
                </OptionButton>
              ))}
            </div>
          </div>
        );

      case 'exploring_contact':
        return renderContactStep(submitForm, true);

      case 'contact_method':
        return (
          <div>
            <h2 className="text-lg font-bold text-[#3D5A47] mb-1 text-center">Preferred contact method?</h2>
            <div className="grid grid-cols-3 gap-2 mt-4">
              {contactMethods.map((method) => (
                <OptionButton
                  key={method.id}
                  selected={formData.preferred_contact === method.id}
                  onClick={() => {
                    updateFormData('preferred_contact', method.id);
                    setTimeout(() => setCurrentStep('contact_details'), 150);
                  }}
                >
                  <div className="flex flex-col items-center py-2">
                    <method.icon className={`w-6 h-6 mb-1 ${
                      formData.preferred_contact === method.id ? 'text-[#B5D43B]' : 'text-gray-400'
                    }`} />
                    <span className="text-xs font-medium text-[#3D5A47]">{method.label}</span>
                  </div>
                </OptionButton>
              ))}
            </div>
          </div>
        );

      case 'contact_details':
        return renderContactStep(() => setCurrentStep('message_step'));

      case 'message_step':
        return (
          <div>
            <h2 className="text-lg font-bold text-[#3D5A47] mb-1 text-center">Briefly tell us what you need</h2>
            <p className="text-gray-600 text-sm text-center mb-4">Optional</p>
            <Textarea
              placeholder="How can we help?"
              value={formData.message}
              onChange={(e) => updateFormData('message', e.target.value)}
              className="min-h-[100px]"
            />
            <Button
              onClick={submitForm}
              className="w-full mt-4 bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47]"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  // Reusable contact step renderer with OTP verification
  const renderContactStep = (onContinue, showSubmit = false) => {
    const needsOTP = isOTPRequired();
    const phoneProvided = formData.phone && formData.phone.length >= 10;
    const canProceed = formData.first_name &&
      ((formData.preferred_contact === 'email' && formData.email) ||
       (formData.preferred_contact !== 'email' && formData.phone));
    
    // If OTP is required and not verified, show verification UI
    if (needsOTP && phoneProvided && !otpVerified) {
      return (
        <div>
          <h2 className="text-lg font-bold text-[#3D5A47] mb-1 text-center">Verify your mobile</h2>
          <p className="text-gray-600 text-sm text-center mb-4">
            {otpSent 
              ? `Enter the 6-digit code sent to ${formData.phone}`
              : "We'll send a verification code to confirm your number"}
          </p>
          
          {!otpSent ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="verify_phone" className="text-[#3D5A47] text-sm">Mobile number</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="verify_phone"
                    type="tel"
                    placeholder="0412 345 678"
                    value={formData.phone}
                    onChange={(e) => updateFormData('phone', e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              
              {otpError && (
                <p className="text-red-500 text-sm text-center">{otpError}</p>
              )}
              
              <Button
                onClick={sendOTP}
                className="w-full bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47]"
                disabled={otpLoading || !formData.phone}
              >
                {otpLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send verification code'
                )}
              </Button>
              
              {/* Skip verification for email-only */}
              {formData.preferred_contact === 'email' && (
                <button
                  onClick={() => setOtpVerified(true)}
                  className="w-full text-sm text-gray-500 hover:text-[#B5D43B]"
                >
                  Continue with email only
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label htmlFor="otp_code" className="text-[#3D5A47] text-sm">Verification code</Label>
                <Input
                  id="otp_code"
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={otpCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtpCode(val);
                    setOtpError('');
                  }}
                  className="text-center text-xl tracking-widest"
                  maxLength={6}
                />
              </div>
              
              {otpError && (
                <p className="text-red-500 text-sm text-center">{otpError}</p>
              )}
              
              <Button
                onClick={verifyOTP}
                className="w-full bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47]"
                disabled={otpLoading || otpCode.length !== 6}
              >
                {otpLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify'
                )}
              </Button>
              
              <div className="flex justify-between items-center text-sm">
                <button
                  onClick={() => {
                    setOtpSent(false);
                    setOtpCode('');
                    setOtpError('');
                  }}
                  className="text-gray-500 hover:text-[#B5D43B]"
                >
                  Change number
                </button>
                <button
                  onClick={sendOTP}
                  disabled={resendCountdown > 0 || otpLoading}
                  className={`${resendCountdown > 0 ? 'text-gray-400' : 'text-[#B5D43B] hover:underline'}`}
                >
                  {resendCountdown > 0 ? `Resend in ${resendCountdown}s` : 'Resend code'}
                </button>
              </div>
            </div>
          )}
        </div>
      );
    }
    
    // Show verified badge if OTP was verified
    const VerifiedBadge = () => otpVerified && (
      <div className="flex items-center gap-1 text-xs text-green-600 mt-1">
        <CheckCircle className="w-3 h-3" />
        <span>Verified</span>
      </div>
    );
    
    return (
      <div>
        <h2 className="text-lg font-bold text-[#3D5A47] mb-1 text-center">Your contact details</h2>
        <p className="text-gray-600 text-sm text-center mb-4">So we can get back to you</p>
        <div className="space-y-3">
          {/* Contact Method */}
          <div>
            <Label className="text-[#3D5A47] text-sm mb-2 block">Preferred contact method</Label>
            <div className="grid grid-cols-3 gap-2">
              {contactMethods.map((method) => (
                <button
                  key={method.id}
                  onClick={() => {
                    updateFormData('preferred_contact', method.id);
                    // Reset OTP if changing to non-phone method
                    if (method.id === 'email') {
                      setOtpSent(false);
                      setOtpCode('');
                      setOtpVerified(false);
                    }
                  }}
                  className={`p-2 rounded-lg border-2 flex flex-col items-center transition-all ${
                    formData.preferred_contact === method.id
                      ? 'border-[#B5D43B] bg-[#B5D43B]/10'
                      : 'border-gray-200 hover:border-[#B5D43B]/50'
                  }`}
                >
                  <method.icon className={`w-4 h-4 ${
                    formData.preferred_contact === method.id ? 'text-[#B5D43B]' : 'text-gray-400'
                  }`} />
                  <span className="text-xs mt-1 text-[#3D5A47]">{method.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* First Name */}
          <div>
            <Label htmlFor="first_name" className="text-[#3D5A47] text-sm">First name *</Label>
            <div className="relative mt-1">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="first_name"
                placeholder="John"
                value={formData.first_name}
                onChange={(e) => updateFormData('first_name', e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="email" className="text-[#3D5A47] text-sm">
              Email {formData.preferred_contact === 'email' ? '*' : ''}
            </Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="email"
                type="email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={(e) => updateFormData('email', e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Phone */}
          <div>
            <Label htmlFor="phone" className="text-[#3D5A47] text-sm">
              Mobile {formData.preferred_contact !== 'email' ? '*' : ''}
            </Label>
            <div className="relative mt-1">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="phone"
                type="tel"
                placeholder="0412 345 678"
                value={formData.phone}
                onChange={(e) => updateFormData('phone', e.target.value)}
                className="pl-9"
              />
              <VerifiedBadge />
            </div>
          </div>

          {/* Best Time */}
          <div>
            <Label className="text-[#3D5A47] text-sm mb-2 block">Best time to contact</Label>
            <div className="grid grid-cols-4 gap-1">
              {bestTimeOptions.map((time) => (
                <button
                  key={time.id}
                  onClick={() => updateFormData('best_time', time.id)}
                  className={`p-2 rounded-lg border text-xs transition-all ${
                    formData.best_time === time.id
                      ? 'border-[#B5D43B] bg-[#B5D43B]/10 text-[#3D5A47]'
                      : 'border-gray-200 hover:border-[#B5D43B]/50 text-gray-600'
                  }`}
                >
                  {time.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <Button
          onClick={showSubmit ? submitForm : onContinue}
          className="w-full mt-4 bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47]"
          disabled={!canProceed || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Sending...
            </>
          ) : showSubmit ? (
            'Submit Request'
          ) : (
            <>Continue <ArrowRight className="w-4 h-4 ml-2" /></>
          )}
        </Button>
      </div>
    );
  };

  return (
    <div className={isFullPage ? 'max-w-lg mx-auto' : ''}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          {currentStep !== 'welcome' && (
            <button
              onClick={goBack}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-gray-600" />
            </button>
          )}
          <div>
            <h3 className="font-semibold text-[#3D5A47] text-sm">Supa Help</h3>
            <p className="text-xs text-gray-500">Existing customers first — we're here to help</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {currentStep !== 'welcome' && (
            <button
              onClick={startOver}
              className="text-xs text-gray-500 hover:text-[#B5D43B]"
            >
              Start over
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
          <p className="text-xs mt-1">Or email us at info@supaenergyhub.com.au</p>
        </div>
      )}

      {/* Step Content */}
      {renderStepContent()}
    </div>
  );
};

export default SupaHelpFlow;
